'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

interface UseUrlTabStateOptions<T extends string> {
  allowedTabs: readonly T[];
  defaultTab: T;
  paramName?: string;
}

export function useUrlTabState<T extends string>({
  allowedTabs,
  defaultTab,
  paramName = 'tab',
}: UseUrlTabStateOptions<T>) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const allowedSet = useMemo(() => new Set(allowedTabs), [allowedTabs]);

  const readTabFromUrl = useCallback((): T => {
    const tab = searchParams.get(paramName) as T | null;
    return tab && allowedSet.has(tab) ? tab : defaultTab;
  }, [allowedSet, defaultTab, paramName, searchParams]);

  const [activeTab, setActiveTabState] = useState<T>(() => readTabFromUrl());

  useEffect(() => {
    const nextTab = readTabFromUrl();
    setActiveTabState((currentTab) => (currentTab === nextTab ? currentTab : nextTab));
  }, [readTabFromUrl]);

  const setActiveTab = useCallback(
    (nextTab: T) => {
      setActiveTabState(nextTab);

      const params = new URLSearchParams(searchParams.toString());
      if (nextTab === defaultTab) {
        params.delete(paramName);
      } else {
        params.set(paramName, nextTab);
      }

      const nextQuery = params.toString();
      router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname, { scroll: false });
    },
    [defaultTab, paramName, pathname, router, searchParams],
  );

  return [activeTab, setActiveTab] as const;
}
